import _ from 'lodash'
import { hoursInYear } from '../../../util/hours-in-year'
import { createHourSeriesExport } from '../../../building-energy-model/create-hourseries-export'
import { HourSeriesResult } from '../../../building-energy-model/hour-series-calculator'
import {
  ConsumptionEnergyType,
  EnergySystemType,
  HourSeriesType,
  Maybe,
  OnOff,
} from '../../../generated/graphql'
import {
  addDaysToDate,
  getMonthStartHourOfYear,
} from '../../common/util/date-util'

type Criteria = {
  range: number[]
  rangeCriteria: number
  criteriaFunc: (rangeItem: number, value: number) => boolean
}

export const HOURS_OF_THE_DAY: Record<string, number[]> = {}

export enum CalibrationHourSeriesType {
  CoolingSystem,
  CoolingSpace,
  CoolingAhu,
  ElectricityBaseLoad,
  ElectricityVentilation,
  ElectricityAppliances,
  ElectricityLighting,
}

export type CalibrationHourSeriesResult = {
  type: CalibrationHourSeriesType
  serie: number[]
}

export const exportToExcel = async (hourSeries: number[][]): Promise<void> => {
  const result: HourSeriesResult[] = []

  for (let i = 0; i < hourSeries.length; i++) {
    const serie = hourSeries[i]

    result[i] = {
      energySystemType: EnergySystemType.DomesticHotWater,
      consumptionEnergyType: ConsumptionEnergyType.DhwCirculationHeating,
      hourSerieType: HourSeriesType.HeatingDemandDensity,
      hourSerieName: `Tuntisarja${i}`,
      hourSerie: serie,
    }
  }

  const workbook = createHourSeriesExport(result)

  const buffer = await workbook.xlsx.writeBuffer()

  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const fileName = `test-${new Date().toISOString()}-export.xlsx`
  saveAs(blob, fileName)
}

export const rangeValues = (
  rangeStart: number,
  rangeEnd: number,
  rangeStep: number,
): number[] => {
  const count = Math.round((rangeEnd - rangeStart) / rangeStep) + 1

  const values = Array<number>(count).fill(rangeStart)
  for (let i = 1; i < values.length; i++) {
    values[i] = values[i - 1] + rangeStep
  }

  return values
}

export const dayHours = (): number[] => rangeValues(0, 23, 1)

export const yearMonths = (): number[] => rangeValues(1, 12, 1)

export const yearHours = (): number[] => rangeValues(0, hoursInYear - 1, 1)

// Like Excel ROUND(<value>/2;0)*2
// Cannot use only LODASH round -> i.e. -3 rounds to -2 (in Excel to -4)
const roundToNearestEvenNumber = (value: number) => {
  const factor = value < 0 ? -1 : 1

  return _.round((factor * value) / 2, 0) * 2 * factor
}

// Like Excel AVERAGEIFS
export const averageIfs = (
  averageArray: number[],
  criteria: Criteria[],
  precision: number | undefined = undefined,
  allowNullValues = false,
): number | null => {
  const inds: number[][] = []
  for (let i = 0; i < criteria.length; i++) {
    const item = criteria[i]
    const ind = _.map(
      _.keys(
        _.pickBy(item.range, (v) => item.criteriaFunc(v, item.rangeCriteria)),
      ),
      Number,
    )
    inds.push(ind)
  }

  const indexes = _.intersection(...inds)

  const result = averageArray.filter((v, i) => indexes.includes(i))

  if (result.length === 0) return allowNullValues ? null : 0

  const average = precision
    ? _.round(_.mean(result), precision)
    : _.mean(result)

  return average
}

export const averageByHour = (
  hourRangeValues: number[],
  valuesSerie: number[],
  cutSerie: number[],
  hoursOfTheDaySerie: number[],
): (number | null)[] => {
  const resultValues = Array<number | null>(hourRangeValues.length).fill(0)

  for (let i = 0; i < resultValues.length; i++) {
    resultValues[i] = averageIfs(valuesSerie, [
      {
        range: hoursOfTheDaySerie,
        rangeCriteria: hourRangeValues[i],
        criteriaFunc: (item, value) => item === value,
      },
      {
        range: cutSerie,
        rangeCriteria: 1,
        criteriaFunc: (item, value) => item === value,
      },
    ])
  }

  return resultValues
}

export const roundSerieToEvenNumbers = (hourSerie: number[]): number[] =>
  hourSerie.map((i) => roundToNearestEvenNumber(i))

export const averageByEvenNumberRange = (
  evenRangeValues: number[],
  evenCriteriaSerie: number[],
  valuesSerie: number[],
  cutSerie: number[],
): (number | null)[] => {
  const evenSerie = roundSerieToEvenNumbers(evenCriteriaSerie)

  const values = Array<number | null>(evenRangeValues.length).fill(0)

  for (let i = 0; i < evenRangeValues.length; i++) {
    values[i] = averageIfs(valuesSerie, [
      {
        range: evenSerie,
        rangeCriteria: evenRangeValues[i],
        criteriaFunc: (item, value) => item === value,
      },
      {
        range: cutSerie,
        rangeCriteria: 1,
        criteriaFunc: (item, value) => item === value,
      },
    ])
  }

  return values
}

export const hoursOfTheDay = (startHour: number): number[] => {
  const key = startHour.toString()

  if (!(key in HOURS_OF_THE_DAY) || HOURS_OF_THE_DAY[key].length === 0) {
    const hoursSerie = Array<number>(hoursInYear).fill(0)

    let start = startHour
    for (let i = 0; i < hoursSerie.length; i++) {
      hoursSerie[i] = start
      start += 1

      if (start % 24 === 0) start = 0
    }

    HOURS_OF_THE_DAY[key] = hoursSerie
  }

  return HOURS_OF_THE_DAY[key]
}

export const calibrationScheduleFactors = (
  startHour: number,
  year: number,
  calibrationWeeklyProfile: Maybe<OnOff[]> | undefined,
  calibrationMonthlyProfile: Maybe<OnOff[]> | undefined,
): number[] => {
  const weekly = calibrationWeeklyProfile
    ? calibrationWeeklyProfile.map((d) => (d === OnOff.On ? 1 : 0))
    : Array.from<number>(Array(7).fill(1))

  const monthly = calibrationMonthlyProfile
    ? calibrationMonthlyProfile.map((m) => (m === OnOff.On ? 1 : 0))
    : Array.from<number>(Array(12).fill(1))

  let currentDate = new Date(year, 0, 1)

  const scheduleSerie = Array<number>(hoursInYear).fill(1)

  let currentDay = (currentDate.getDay() + 6) % 7
  let currentMonth = currentDate.getMonth()

  let start = startHour
  for (let i = 0; i < scheduleSerie.length; i++) {
    scheduleSerie[i] = weekly[currentDay] * monthly[currentMonth]

    start += 1
    if (start % 24 === 0) {
      currentDate = addDaysToDate(currentDate, 1)
      currentDay = (currentDate.getDay() + 6) % 7
      currentMonth = currentDate.getMonth()
      start = 0
    }
  }

  return scheduleSerie
}

export const tempCutSerie = (
  measuredOutsideTempHourSerie: number[],
  calScheduleSerie: number[],
  tempStart: number,
  tempEnd: number,
): number[] => {
  let cutSerie = _.cloneDeep(calScheduleSerie)
  if (cutSerie.length === 0) cutSerie = Array<number>(hoursInYear).fill(1)

  for (let i = 0; i < measuredOutsideTempHourSerie.length; i++) {
    if (cutSerie[i] > 0) {
      const currTemp = roundToNearestEvenNumber(measuredOutsideTempHourSerie[i])

      if (currTemp < tempStart || currTemp > tempEnd) cutSerie[i] = 0
    }
  }

  return cutSerie
}

export const hourCutSerie = (
  calScheduleSerie: number[],
  hourStart: number,
  hourEnd: number,
): number[] => {
  let cutSerie = _.cloneDeep(calScheduleSerie)
  if (cutSerie.length === 0) cutSerie = Array<number>(hoursInYear).fill(1)

  const hourSerie = hoursOfTheDay(0)

  for (let i = 0; i < hourSerie.length; i++) {
    if (cutSerie[i] > 0) {
      if (hourSerie[i] < hourStart || hourSerie[i] > hourEnd) cutSerie[i] = 0
    }
  }

  return cutSerie
}

export const monthsSumSerie = (serie: number[], year: number): number[] => {
  const result = Array<number>(12).fill(0)

  let monthStart = 0
  for (let i = 0; i < result.length; i++) {
    const monthEnd = getMonthStartHourOfYear(year, i + 1) - 1

    result[i] = _.round(_.sum(serie.slice(monthStart, monthEnd)) / 1000, 3)

    monthStart = monthEnd + 1
  }

  return result
}
